import { Controller } from "@hotwired/stimulus"
import { getCurrentLocation, shouldShowLocationRequest } from "@/utils/location_utils"

export default class extends Controller {
  static targets = ["map", "placesInput", "latitudeInput", "longitudeInput"]
  static values = {
    useCurrentLocation: { type: Boolean, default: true },
    defaultLocation: { type: Object, default: { lat: 0, lng: 0 } }
  }

  declare readonly mapTarget: HTMLElement
  declare readonly placesInputTarget: HTMLInputElement
  declare readonly latitudeInputTarget: HTMLInputElement
  declare readonly longitudeInputTarget: HTMLInputElement
  declare readonly useCurrentLocationValue: boolean
  declare readonly defaultLocationValue: { lat: number, lng: number }
  declare map: google.maps.Map
  declare marker: google.maps.Marker | null
  declare placesAutocomplete: google.maps.places.Autocomplete
  declare markerIcon: google.maps.Icon

  async connect() {
    // Initialize the marker icon
    this.markerIcon = {
      url: '/marker-pin.png',
      scaledSize: new google.maps.Size(30, 40),
      anchor: new google.maps.Point(15, 40)
    }
    
    if (window.google?.maps) {
      await this.initializeMap()
      this.initializePlacesAutocomplete()
    } else {
      window.addEventListener('google-maps-loaded', () => {
        this.initializeMap()
        this.initializePlacesAutocomplete()
      })
    }
  }

  private async initializeMap() {
    const defaultLocation = this.defaultLocationValue
    const hasExistingLocation = Boolean(this.latitudeInputTarget.value) && Boolean(this.longitudeInputTarget.value)
    
    let initialLocation = defaultLocation
    let usingCurrentLocation = false
    
    if (hasExistingLocation) {
      // Use the encampment's existing location
      initialLocation = {
        lat: parseFloat(this.latitudeInputTarget.value),
        lng: parseFloat(this.longitudeInputTarget.value)
      }
    } else if (this.useCurrentLocationValue) {
      // Try to use user's current location
      const locationResult = await this.requestUserLocation(defaultLocation)
      initialLocation = locationResult.location
      usingCurrentLocation = locationResult.success
    }

    this.map = new google.maps.Map(this.mapTarget, {
      center: initialLocation,
      zoom: hasExistingLocation || usingCurrentLocation ? 15 : 4,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false
    })

    this.map.addListener('click', (event: google.maps.MapMouseEvent) => {
      const position = event.latLng
      if (!position) return

      this.updateMarkerPosition(position)
      this.clearSearchInput()
    })

    // Create a marker for existing locations or when using current location
    if (hasExistingLocation || usingCurrentLocation) {
      this.updateMarkerPosition(new google.maps.LatLng(initialLocation.lat, initialLocation.lng))
    }
  }

  private async requestUserLocation(defaultLocation: { lat: number, lng: number }): Promise<{ 
    success: boolean, 
    location: { lat: number, lng: number } 
  }> {
    try {
      // No custom confirmation dialog - just use the browser's built-in flow
      const coordinates = await getCurrentLocation(defaultLocation.lat, defaultLocation.lng);
      const location = {
        lat: coordinates.latitude,
        lng: coordinates.longitude
      };
      return { success: true, location };
    } catch (error) {
      return { success: false, location: defaultLocation };
    }
  }

  private initializePlacesAutocomplete() {
    this.placesAutocomplete = new google.maps.places.Autocomplete(this.placesInputTarget, {
      types: ['geocode']
    })

    this.placesAutocomplete.addListener('place_changed', () => {
      const place = this.placesAutocomplete.getPlace()
      if (!place.geometry?.location) return

      this.map.setCenter(place.geometry.location)
      this.map.setZoom(15)
      this.updateMarkerPosition(place.geometry.location)
    })
  }

  private updateMarkerPosition(position: google.maps.LatLng) {
    
    if (!this.marker) {
      this.marker = new google.maps.Marker({
        map: this.map,
        draggable: true,
        icon: this.markerIcon
      })

      this.marker.addListener('dragend', () => {
        const position = this.marker?.getPosition()
        if (!position) return

        this.updateInputs(position)
        this.clearSearchInput()
      })
    }

    this.marker.setPosition(position)
    this.updateInputs(position)
  }

  private updateInputs(position: google.maps.LatLng) {
    this.latitudeInputTarget.value = position.lat().toString()
    this.longitudeInputTarget.value = position.lng().toString()
  }

  private clearSearchInput() {
    this.placesInputTarget.value = ''
  }
} 